/**
 * Desktop click-to-zoom for the PAS product hero, in place.
 *
 * WHY THIS EXISTS
 * Clicking a hero image used to open Horizon's fullscreen lightbox — a second
 * carousel of the same photos, with its own thumbnail rail and fade-in. That is
 * a lot of ceremony for "make this bigger", and on desktop it did not even
 * magnify: assets/drag-zoom-wrapper.js bails out above the mobile breakpoint
 *
 *   connectedCallback() { ... if (!isMobileBreakpoint()) return; ... }
 *
 * and every gesture it implements is bound to touch events, so a mouse never
 * reached it. Desktop "zoom" only ever re-cropped the picture.
 *
 * So on desktop the click now magnifies the image where it already sits.
 *
 * MOBILE IS DELIBERATELY UNTOUCHED. The lightbox is what gives phones
 * pinch-to-zoom (via drag-zoom-wrapper), so below 750px the click still falls
 * through to Horizon's on:click="#<dialog>/open/<index>" and opens the dialog
 * exactly as before.
 *
 * BEHAVIOUR (>= 750px)
 *   - click the image        -> magnify about the clicked point
 *   - move while magnified   -> pan, the point under the cursor follows it
 *   - click again            -> back to normal
 *   - click anywhere else    -> back to normal (carousel arrows keep working)
 *   - Escape                 -> back to normal
 *
 * Geometry is simple here, unlike in the lightbox: the slide is
 * `aspect-ratio: 4/5` with `overflow: hidden` and the image is `object-fit:
 * cover`, so the painted photo always fills the slide box exactly. The
 * element's own rect is the photo — no letterbox maths needed.
 *
 * A CSS transform does not re-pick from srcset; it scales the bitmap the
 * browser already chose for the slide's layout width (~1200px), which looks
 * soft at 2.5x. On first magnify the image is swapped for its
 * `data_max_resolution` source, the same attribute and approach
 * assets/zoom-dialog.js uses (see its loadHighResolutionImage).
 */

import { clamp, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';

const SLIDE = '.product-pas__hero-image--zoomable';

/** The slide currently magnified, or null. Also gates the pan listener. */
let magnified = null;

/**
 * Swap in the full-resolution file the first time a slide is magnified.
 * srcset wins over src, so both it and sizes have to be dropped; preloading
 * first means the swap happens on a decoded image rather than flashing empty.
 *
 * Deliberately not reusing the two nearby helpers: preloadImage() in
 * @theme/utilities is fire-and-forget with no onload to hang the swap on, and
 * zoom-dialog.js's loadHighResolutionImage() replaces the whole <img> node,
 * which here would discard the element the transform and pan state are bound to.
 */
function loadHighResolution(img) {
  const highResUrl = img.getAttribute('data_max_resolution');
  if (!highResUrl || img.dataset.pondHighRes) return;

  img.dataset.pondHighRes = 'pending';
  const preload = new Image();
  preload.onload = () => {
    img.srcset = '';
    img.sizes = '';
    img.src = highResUrl;
    img.dataset.pondHighRes = 'loaded';
  };
  preload.src = highResUrl;
}

/** Point the transform at the cursor, as a percentage of the slide box. */
function applyOrigin(slide, event) {
  const rect = slide.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
  const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
  slide.style.setProperty('--pond-zoom-origin-x', `${x}%`);
  slide.style.setProperty('--pond-zoom-origin-y', `${y}%`);
}

/**
 * Bound only for the duration of a zoom. Binding it for the page lifetime would
 * run a hit test on every mouse move across the whole page for something that
 * is idle almost all of the time.
 */
function handlePan(event) {
  if (!magnified) return;

  if (!magnified.contains(event.target)) {
    magnified.removeAttribute('data-pond-panning');
    return;
  }

  magnified.setAttribute('data-pond-panning', '');
  applyOrigin(magnified, event);
}

function zoomOut() {
  if (!magnified) return;
  magnified.removeAttribute('data-pond-zoomed');
  magnified.removeAttribute('data-pond-panning');
  magnified.style.removeProperty('--pond-zoom-origin-x');
  magnified.style.removeProperty('--pond-zoom-origin-y');
  magnified = null;
  document.removeEventListener('mousemove', handlePan);
}

function zoomIn(slide, event) {
  zoomOut();
  magnified = slide;
  applyOrigin(slide, event);
  slide.setAttribute('data-pond-zoomed', '');

  const img = slide.querySelector('img');
  if (img) loadHighResolution(img);

  document.addEventListener('mousemove', handlePan);
}

// Bound to `window`, not `document`, and this matters.
//
// Horizon resolves every declarative on:click through one delegated listener
// registered in assets/component.js:
//
//   document.addEventListener(eventName, handler, { capture: true })
//
// Two capture listeners on the SAME node run in registration order, and
// component.js is loaded first — so a document-level capture listener here would
// still fire after Horizon had already opened the lightbox. stopPropagation()
// cannot undo a handler that has already run.
//
// The capture path is window -> document -> ... -> target, so a listener on
// window runs strictly before any listener on document. Stopping propagation
// here means the click never reaches Horizon's delegate, and the dialog that
// used to open on desktop simply does not.
window.addEventListener(
  'click',
  (event) => {
    // Below the breakpoint this does nothing and the lightbox opens as before.
    if (!isDesktopBreakpoint()) return;

    const slide = event.target.closest(SLIDE);

    // A click elsewhere (carousel arrow, buy box, background) settles the zoom
    // but must keep propagating so that control still does its job.
    if (!slide) {
      zoomOut();
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (slide === magnified) zoomOut();
    else zoomIn(slide, event);
  },
  true
);

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') zoomOut();
});

// Dropping below the breakpoint hands control back to the lightbox.
mediaQueryLarge.addEventListener('change', (event) => {
  if (!event.matches) zoomOut();
});
